import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { where, documentId } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection, useDocument } from '../../lib/firestore'
import { useTourAccess } from '../../lib/tours'
import { useIsMobile } from '../../lib/useMediaQuery'
import MobileHeader from '../../components/mobile/MobileHeader'
import { TAB_BAR_INSET } from '../../components/mobile/TabBar'
import { countdown, formatDayLong, formatTime } from '../../lib/format'
import type {
  GuestListItem,
  ItineraryItem,
  Show,
  SupportAct,
  Tour,
  WithId,
} from '../../types/models'
import Icon from '../../components/Icon'
import ShareSheet from '../../components/ShareSheet'
import Empty from '../../components/Empty'
import VenueMap from '../../components/VenueMap'
import DeleteConfirm from '../../components/DeleteConfirm'
import ShowDialog from '../../dialogs/ShowDialog'
import { addGuest, deleteGuest, deleteShow } from '../../lib/mutations'

/**
 * A single show.
 *
 * The page a tour manager opens on the day: where it is, when everything
 * happens, who to call at the venue, and who's on the list. The running order
 * is read from the show's own `itineraryItems` rather than the fields on the
 * show, because those items are what the day sheet and the push notifications
 * actually use — if the two ever disagree, this shows the one that's live.
 */
const TIMING_ORDER = [
  'venueAccess',
  'loadIn',
  'soundcheck',
  'doors',
  'headline',
  'packOut',
] as const

export default function ShowDetail() {
  const { tourId, showId } = useParams<{ tourId: string; showId: string }>()
  const navigate = useNavigate()
  const { uid } = useAuth()

  const { data: tour } = useDocument<Tour>('tours', tourId)
  const { data: show, loading } = useDocument<Show>('shows', showId)
  const access = useTourAccess(tour, uid)
  const canEdit = access?.canEdit ?? false

  // Scoped by tourId, then narrowed to this show in memory.
  //
  // The rules resolve a tour lookup per document and refuse any query they
  // cannot satisfy — "every client query against these MUST constrain tourId"
  // is the contract. Filtering on `showId` alone was rejected outright, so the
  // running order came back empty and the page reported no times set on shows
  // whose timings were sitting in the database, correct and unreachable.
  //
  // Narrowing here rather than adding a second `where` avoids a composite
  // index for what is at most a few dozen documents.
  const tourItems = useCollection<ItineraryItem>(
    'itineraryItems',
    tourId ? [where('tourId', '==', tourId)] : null,
  )
  const items = {
    ...tourItems,
    data: tourItems.data.filter((item) => item.showId === showId),
  }
  const guests = useCollection<GuestListItem>(
    showId ? `shows/${showId}/guestlist` : 'shows',
    showId ? [] : null,
  )
  const supportActs = useCollection<SupportAct>(
    'supportActs',
    show?.supportActIds?.length ? [where(documentId(), 'in', show.supportActIds.slice(0, 30))] : null,
  )

  const [editing, setEditing] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const isMobile = useIsMobile()

  if (loading) return <div style={{ padding: 30 }} />

  if (!show) {
    return (
      <div style={{ padding: 30 }}>
        <Empty title="Show not found" message="It may have been deleted." />
      </div>
    )
  }

  const zone = show.timezone
  const runningOrder = [...items.data]
    .filter((item) => item.isShowTiming)
    .sort((a, b) => {
      const ai = TIMING_ORDER.indexOf(a.type as (typeof TIMING_ORDER)[number])
      const bi = TIMING_ORDER.indexOf(b.type as (typeof TIMING_ORDER)[number])
      if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi)
      return a.timeUTC.toMillis() - b.timeUTC.toMillis()
    })
  const otherItems = [...items.data]
    .filter((item) => !item.isShowTiming)
    .sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())

  const mapQuery = encodeURIComponent(
    [show.venueName, show.venueAddress, show.city, show.country].filter(Boolean).join(', '),
  )

  return (
    <>
      {isMobile && (
        <MobileHeader
          title={show.city}
          back={`/tours/${tourId}/shows`}
          action={
            canEdit
              ? { icon: 'edit', label: 'Edit show', onClick: () => setEditing(true) }
              : undefined
          }
        />
      )}
      <div
        style={{
          padding: isMobile
            ? `18px var(--gutter) ${TAB_BAR_INSET}px`
            : '24px var(--gutter) 72px',
        }}
      >
      <div className="sections" style={{ maxWidth: 1100, margin: '0 auto', gap: 26 }}>
        {!isMobile && (
          <Link
            to={`/tours/${tourId}/shows`}
            className="btn btn-quiet"
            style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
          >
            <Icon name="chevronLeft" size={15} /> All shows
          </Link>
        )}

        {/* ------------------------------------------------------- header */}
        <header className="spread" style={{ alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div className="stack" style={{ gap: 4, minWidth: 0 }}>
            <span className="t-eyebrow">{formatDayLong(show.date, zone)}</span>
            <h1 className="t-display-xl">{show.city}</h1>
            <span className="t-body dim truncate">
              {show.venueName}
              {show.country ? ` · ${show.country}` : ''}
            </span>
          </div>

          <div className="row" style={{ gap: 8 }}>
            <span className="chip chip-accent">{countdown(show.date)}</span>
            {/* Sharing is a read action, so it is offered to viewers too. The
                sheet gates the documents on their own plan features. */}
            <button className="btn btn-secondary" onClick={() => setSharing(true)}>
              <Icon name="share" size={15} /> Share
            </button>
            {canEdit && !isMobile && (
              <button className="btn btn-secondary" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} /> Edit show
              </button>
            )}
          </div>
        </header>

        <div
          className="responsive-grid"
          style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 22, alignItems: 'start' }}
        >
          {/* -------------------------------------------- running order */}
          <div className="stack" style={{ gap: 22 }}>
            <section className="stack" style={{ gap: 10 }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Running order</h2>
              {runningOrder.length === 0 ? (
                <p className="panel panel-pad t-caption">
                  No times set for this show yet.{' '}
                  {canEdit && (
                    <button className="accent" onClick={() => setEditing(true)}>
                      Set them
                    </button>
                  )}
                </p>
              ) : (
                <div className="panel rows">
                  {runningOrder.map((item) => (
                    <div
                      key={item.id}
                      className="spread"
                      style={{ padding: '14px 18px', gap: 12 }}
                    >
                      <span
                        className="row"
                        style={{
                          gap: 12,
                          minWidth: 0,
                          fontWeight: item.type === 'headline' ? 700 : 400,
                          color:
                            item.type === 'headline' ? 'var(--accent-text)' : undefined,
                        }}
                      >
                        {item.title}
                      </span>
                      <span
                        className="t-timecode"
                        style={{
                          color: item.needsTime
                            ? 'var(--text-tertiary)'
                            : item.type === 'headline'
                              ? 'var(--accent-text)'
                              : undefined,
                        }}
                      >
                        {item.needsTime ? '--:--' : formatTime(item.timeUTC, item.timezone)}
                      </span>
                    </div>
                  ))}
                  {show.headlinerSetDurationMinutes ? (
                    <div className="spread" style={{ padding: '11px 18px' }}>
                      <span className="t-caption">Set length</span>
                      <span className="t-spec-caption dim">
                        {show.headlinerSetDurationMinutes} min
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </section>

            {otherItems.length > 0 && (
              <section className="stack" style={{ gap: 10 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Also on this show</h2>
                <div className="panel rows">
                  {otherItems.map((item) => (
                    <div key={item.id} className="spread" style={{ padding: '13px 18px', gap: 12 }}>
                      <span className="truncate">{item.title}</span>
                      <span className="t-timecode">
                        {item.needsTime ? '--:--' : formatTime(item.timeUTC, item.timezone)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <GuestList
              showId={show.id}
              guests={guests.data}
              canEdit={canEdit}
              loading={guests.loading}
            />
          </div>

          {/* --------------------------------------------------- venue */}
          <div className="stack" style={{ gap: 22 }}>
            <section className="stack" style={{ gap: 10 }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Venue</h2>

              <VenueMap
                showId={show.id}
                query={[show.venueName, show.venueAddress, show.city, show.country]
                  .filter(Boolean)
                  .join(', ')}
                parts={{
                  venueName: show.venueName,
                  venueAddress: show.venueAddress,
                  city: show.city,
                  country: show.country,
                }}
                lat={show.venueLat}
                lon={show.venueLon}
                label={show.venueName}
              />

              <div className="panel panel-pad stack" style={{ gap: 14 }}>
                <div className="stack" style={{ gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: '1.02rem' }}>{show.venueName}</span>
                  {show.venueAddress && (
                    <span className="t-caption">{show.venueAddress}</span>
                  )}
                  <span className="t-caption">
                    {[show.city, show.country].filter(Boolean).join(', ')}
                  </span>
                </div>

                {show.venueAddress && (
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <a
                      className="btn btn-secondary"
                      href={`https://maps.google.com/?q=${mapQuery}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <Icon name="mapPin" size={15} /> Open in Maps
                    </a>
                    <CopyButton
                      value={[show.venueName, show.venueAddress, show.city]
                        .filter(Boolean)
                        .join(', ')}
                      label="Copy address"
                    />
                  </div>
                )}

                {zone && (
                  <div className="stack" style={{ gap: 1 }}>
                    <span className="t-eyebrow">Venue timezone</span>
                    <span className="t-spec-caption dim">{zone}</span>
                  </div>
                )}

                {show.scanCode && (
                  <div className="stack" style={{ gap: 1 }}>
                    <span className="t-eyebrow">Scan code</span>
                    <span className="t-spec" style={{ letterSpacing: 2 }}>
                      {show.scanCode}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {(show.contactName || show.contactEmail || show.contactPhone) && (
              <section className="stack" style={{ gap: 10 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Venue contact</h2>
                <div className="panel panel-pad stack" style={{ gap: 8 }}>
                  {show.contactName && (
                    <span style={{ fontWeight: 600 }}>{show.contactName}</span>
                  )}
                  {show.contactEmail && (
                    <a className="row accent" style={{ gap: 8 }} href={`mailto:${show.contactEmail}`}>
                      <Icon name="account" size={15} />
                      <span className="truncate">{show.contactEmail}</span>
                    </a>
                  )}
                  {show.contactPhone && (
                    <a className="row accent" style={{ gap: 8 }} href={`tel:${show.contactPhone}`}>
                      <Icon name="people" size={15} />
                      {show.contactPhone}
                    </a>
                  )}
                </div>
              </section>
            )}

            {supportActs.data.length > 0 && (
              <section className="stack" style={{ gap: 10 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Support</h2>
                <div className="panel rows">
                  {supportActs.data.map((act) => (
                    <div key={act.id} className="spread" style={{ padding: '12px 16px' }}>
                      <span className="truncate">{act.name}</span>
                      <span className="chip">{act.type}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {canEdit && (
              <button
                className="btn btn-danger"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setDeleting(true)}
              >
                <Icon name="trash" size={15} /> Delete show
              </button>
            )}
          </div>
        </div>
      </div>

      <ShareSheet
        open={sharing}
        onClose={() => setSharing(false)}
        title={`${show.city} · ${show.venueName}`}
        subtitle={formatDayLong(show.date, zone)}
        url={`/tours/${tourId}/shows/${show.id}`}
        tourId={tourId ?? ''}
        showId={show.id}
      />

      {editing && uid && tourId && (
        <ShowDialog
          open
          show={show}
          tourId={tourId}
          userId={uid}
          onClose={() => setEditing(false)}
        />
      )}

      {deleting && (
        <DeleteConfirm
          label={`${show.city} · ${show.venueName}`}
          detail="The show's running-order items come off the itinerary too."
          onCancel={() => setDeleting(false)}
          onConfirm={async () => {
            await deleteShow(show)
            navigate(`/tours/${tourId}/shows`, { replace: true })
          }}
        />
      )}
      </div>
    </>
  )
}

/* ------------------------------------------------------------- guest list */

function GuestList({
  showId,
  guests,
  canEdit,
  loading,
}: {
  showId: string
  guests: WithId<GuestListItem>[]
  canEdit: boolean
  loading: boolean
}) {
  const [name, setName] = useState('')
  const [plusOnes, setPlusOnes] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [removing, setRemoving] = useState<WithId<GuestListItem> | null>(null)

  // What the door actually needs: names on the list plus their plus-ones.
  const headcount = guests.reduce(
    (total, guest) => total + 1 + (Number(guest.additionalGuests || '0') || 0),
    0,
  )

  async function submit() {
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      await addGuest(showId, { name, additionalGuests: plusOnes, note })
      setName('')
      setPlusOnes('')
      setNote('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="spread">
        <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>Guest list</h2>
        <span className="t-caption">
          {guests.length} name{guests.length === 1 ? '' : 's'} · {headcount} on the door
        </span>
      </div>

      {!loading && guests.length === 0 && (
        <p className="panel panel-pad t-caption">Nobody on the list yet.</p>
      )}

      {guests.length > 0 && (
        <div className="panel rows">
          {guests.map((guest) => {
            const extra = Number(guest.additionalGuests || '0') || 0
            return (
              <div key={guest.id} className="row" style={{ padding: '12px 16px', gap: 12 }}>
                <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ fontWeight: 600 }}>
                    {guest.name}
                    {extra > 0 && (
                      <span className="t-spec-caption faint" style={{ marginLeft: 6 }}>
                        +{extra}
                      </span>
                    )}
                  </span>
                  {guest.note && <span className="t-caption truncate">{guest.note}</span>}
                </span>
                {canEdit && (
                  <button
                    aria-label={`Remove ${guest.name}`}
                    title="Remove"
                    onClick={() => setRemoving(guest)}
                    style={{ color: 'var(--text-tertiary)', padding: 4 }}
                  >
                    <Icon name="close" size={15} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {canEdit && (
        <div className="panel panel-pad stack" style={{ gap: 8 }}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
            <span className="t-eyebrow">Guest name</span>
            <span className="t-eyebrow">Additional guests</span>
            <input
              className="field"
              aria-label="Guest name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void submit()
              }}
            />
            <input
              className="field"
              type="number"
              min={0}
              aria-label="Additional guests"
              value={plusOnes}
              onChange={(event) => setPlusOnes(event.target.value)}
            />
          </div>
          <input
            className="field"
            aria-label="Note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit()
            }}
          />
          <button
            className="btn btn-primary"
            style={{ alignSelf: 'flex-start' }}
            disabled={!name.trim() || busy}
            onClick={() => void submit()}
          >
            {busy ? 'Adding…' : 'Add to list'}
          </button>
        </div>
      )}

      {removing && (
        <DeleteConfirm
          label={removing.name}
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            await deleteGuest(showId, removing.id)
            setRemoving(null)
          }}
        />
      )}
    </section>
  )
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      className="btn btn-secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setCopied(true)
          window.setTimeout(() => setCopied(false), 1800)
        } catch {
          // Clipboard is blocked in some contexts; the address is on screen.
        }
      }}
    >
      {copied ? 'Copied' : label}
    </button>
  )
}
